"use client";

import { sanitizeImageUrl } from "@/components/molecules/image-gallery";
import QuantitySelector from "@/components/atoms/quantity-selector";
import { Toast, toast } from "@/components/atoms/toast";
import ProductConfigurationModal from "@/components/modules/account/product-configuration-modal";
import { Badge } from "@/components/ui/badge";
import ProductBadge from "@/components/atoms/product-badge";
import { formatProductQuantity } from "@/lib/product-quantity-label";
import { cn, formatPrice, isSaleProduct } from "@/lib/utils";
import { Trash2Icon } from "lucide-react";
import { StoreProduct } from "@medusajs/types";
import Image from "next/image";
import { useRemoveCartItem, useUpdateCartItem } from "@/hooks/useCartMutations";
import { StoreCartLineItem } from "@medusajs/types";
import { __, _x } from "@/lib/i18n/functions";
import { Locales } from "@/lib/i18n";
import { ProductDocument } from "@/prismicio-types";
import CartProductItem from "@/components/organisms/cart-product-item";
import { useState } from "react";
import {
  getCartQuantityStep,
  normalizeConditioning,
  resolveProductConditioning,
  toLotPrice,
  toPiecePrice,
} from "@/lib/product-units";

type PaymentProductCardProps = {
  medusaProduct: StoreProduct;
  quantity?: number;
  cartItem: StoreCartLineItem | undefined;
  locale: Locales;
  prismicProduct: ProductDocument;
};

const PaymentProductCard = ({ medusaProduct, quantity, cartItem, locale, prismicProduct }: PaymentProductCardProps) => {
  const [count, setCount] = useState(quantity || 1);

  const { removeItem } = useRemoveCartItem({
    onSuccess: () => {
      toast.success({ title: __("Item removed from cart!", locale) });
    },
    onError: (error) => {
      console.error("Error removing item from cart:", error, cartItem);
      toast.error({ title: __("Error removing item from cart", locale) });
    },
  });

  const { updateQuantity } = useUpdateCartItem({
    onSuccess: () => {
      toast.success({ title: __("Quantity updated successfully", locale) });
    },
    onError: (err) => {
      console.error("Error updating quantity", err);
      toast.error({ title: __("Failed to update quantity", locale) });
    },
  });

  const handleQuantityChange = (newQuantity: number) => {
    if (cartItem?.id && newQuantity !== cartItem.quantity) {
      updateQuantity(cartItem.id, newQuantity);
    }
  };

  const productConditioning = resolveProductConditioning(
    medusaProduct?.metadata?.conditioning,
    medusaProduct?.metadata?.conditioning_bac
  );
  const quantityStep = getCartQuantityStep(productConditioning);
  const physicalPieceCount = normalizeConditioning(productConditioning);
  const isFullImage = prismicProduct?.data?.image_full_size_card;
  const isRange = medusaProduct?.metadata?.is_surface_based as boolean;
  const isPriceDynamic = medusaProduct?.metadata?.is_price_dynamic as boolean;
  const hasMultipleVariants = (medusaProduct?.variants?.length ?? 0) > 1;

  // Variant from cart (ground truth)
  const cartVariant = hasMultipleVariants
    ? medusaProduct?.variants?.find((v) => v.id === cartItem?.variant_id)
    : medusaProduct?.variants?.[0];

  // ── Surface of the cart variant — passed as defaultSurface to the modal ──
  // This ensures that when the user opens the configuration modal on the cart
  // page, the dropdown shows the surface that is actually in the cart — not a
  // stale value left in the Zustand store from a previous PDP visit.
  const cartVariantSurface = cartVariant?.metadata?.["surface"] as number | undefined;

  const cartVariantPrice = cartVariant?.calculated_price?.calculated_amount ?? 0;
  const singleVariantPrice = medusaProduct?.variants?.[0]?.calculated_price?.calculated_amount || 0;
  const productPrice = hasMultipleVariants ? cartVariantPrice : singleVariantPrice;
  const pricePerPiece = toPiecePrice(productPrice, productConditioning);

  const value = formatPrice(toLotPrice(singleVariantPrice, productConditioning));

  // Formatted prices (used in desktop list item)
  const formattedVariantUnitPrice = hasMultipleVariants
    ? formatPrice(toLotPrice(cartVariantPrice, productConditioning), { showDecimals: true })
    : value;

  const formattedVariantTotalPrice = hasMultipleVariants
    ? formatPrice(cartVariantPrice * count, { showDecimals: true })
    : formatPrice(singleVariantPrice * count);

  // Badge label for "Price per unit" when count > 1
  const selectedVariantPriceBadge = hasMultipleVariants ? formattedVariantUnitPrice : value;

  return (
    <>
      <li className="flex items-center gap-5.5 max-md:hidden">
        <figure className="bg-product-card relative flex h-[110px] w-[120px] shrink-0 items-center justify-center overflow-hidden rounded-md">
          {isFullImage ? (
            <Image
              className="absolute object-cover mix-blend-multiply"
              src={
                sanitizeImageUrl(medusaProduct?.images?.at(0)?.url || "") ||
                prismicProduct?.data.images[0]?.image.url ||
                prismicProduct?.data.featuredImage.url ||
                ""
              }
              alt={prismicProduct?.data.title || medusaProduct.title}
              fill
            />
          ) : (
            <div className="relative size-20">
              <Image
                className="absolute object-contain mix-blend-multiply"
                src={
                  sanitizeImageUrl(medusaProduct?.images?.at(0)?.url || "") ||
                  prismicProduct?.data.images[0]?.image.url ||
                  prismicProduct?.data.featuredImage.url ||
                  ""
                }
                alt={prismicProduct?.data.title || medusaProduct.title}
                fill
              />
            </div>
          )}
        </figure>

        <dl className="flex w-full items-center justify-between">
          <div className="space-y-2">
            <h3 className="font-cabinet-grotesk text-2xl font-bold">
              {prismicProduct?.data.title || medusaProduct.title}
            </h3>

            {isRange && (
              <ProductConfigurationModal
                products={[prismicProduct]}
                medusaProducts={{ [medusaProduct.id]: medusaProduct }}
                locale={locale}
                // Seed the modal with the surface that is actually in the cart.
                // Without this, the modal would show whatever surface was last
                // written to the store (e.g. from a PDP visit), not the cart value.
                defaultSurface={cartVariantSurface}
                hideWhenConfigured
              />
            )}

            <div className="flex items-center gap-2">
              {isRange && hasMultipleVariants && isSaleProduct(medusaProduct) && (
                <Badge variant="festi">{__("To sell", locale)}</Badge>
              )}
              {isRange && hasMultipleVariants && count > 1 && (
                <ProductBadge
                  text={_x("Price : {{ selectedVariantPriceBadge }}", { selectedVariantPriceBadge }, locale)}
                  className="w-fit text-sm font-normal text-inherit"
                />
              )}
            </div>

            {!isRange && !isPriceDynamic && (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  {count > 1 && (
                    <ProductBadge
                      text={_x("Price : {{ value }}", { value }, locale)}
                      className="w-fit text-sm font-normal text-inherit"
                    />
                  )}
                </div>
                {isSaleProduct(medusaProduct) && <Badge variant="festi">{__("To sell", locale)}</Badge>}
                <div className="flex items-center gap-2">
                  {physicalPieceCount > 1 && (
                    <>
                      <dt className="sr-only">{__("Price per piece", locale)}</dt>
                      <dd className="text-primary-light text-sm capitalize">
                        {formatPrice(pricePerPiece, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                        /{__("piece", locale)}
                      </dd>
                    </>
                  )}
                  {physicalPieceCount > 1 && (
                    <ProductBadge className="max-w-full whitespace-normal text-right" text={formatProductQuantity(count, productConditioning, locale)} />
                  )}
                </div>
              </>
            )}
          </div>

          <div className={cn("flex flex-col items-end gap-1", isRange && "gap-2")}>
            {!isRange && !isPriceDynamic ? (
              <>
                <dt className="sr-only">{__("Product Price", locale)}</dt>
                <dd>
                  <h3 className="font-cabinet-grotesk text-2xl font-bold">{formatPrice(singleVariantPrice * count)}</h3>
                </dd>
              </>
            ) : hasMultipleVariants ? (
              // Multi-variant range: price derived from the cart variant, not the store
              <p className="font-cabinet-grotesk text-2xl font-bold">{formattedVariantTotalPrice}</p>
            ) : (
              <Toast
                id="product-hero"
                type="info"
                className="min-[384px]:min-w-none h-fit max-w-none py-3 shadow-none"
                title={__("Price on demand", locale)}
              />
            )}

            <div className="flex items-center gap-3.5">
              <button
                onClick={() => removeItem(cartItem?.id || "")}
                className="group cursor-pointer rounded-full p-1 transition-all duration-300 ease-in-out"
              >
                <Trash2Icon size={20} className="group-hover:text-pink-primary text-primary-light" />
              </button>
              <QuantitySelector
                locale={locale}
                variant="secondary"
                className="bg-white"
                count={count}
                setCount={setCount}
                step={quantityStep}
                onQuantityChange={handleQuantityChange}
              />
            </div>
          </div>
        </dl>
      </li>

      {cartItem && (
        <CartProductItem
          locale={locale}
          medusaProduct={medusaProduct}
          prismicProduct={prismicProduct}
          cartItem={cartItem}
          count={count}
          setCount={setCount}
          isFullImage={isFullImage}
          className="md:hidden"
        />
      )}
    </>
  );
};

export default PaymentProductCard;
